import { Builder } from '@builder.io/react';
import React, { useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Telemetry } from '@videoblocks/kafka-rest-client';

import VideoStockItemCard from '../../../app/Search/components/card/VideoStockItemCard';
import { getCategoriesForContentType } from '../../../app/Search/entities/VideoSearchFilterOptions';
import { usePrevious } from '../../../common/hooks/usePrevious';
import { isEqual } from '../../../common/utils';
import {
  browseCategoriesMediaTypesOptions,
  defaultSortOptions,
  stockItemCardResultOptions,
  videoSortOptions,
} from '../../constants';
import { BottomCTASubFields } from '../../constants';
import {
  useVideoGridContentFetching,
  type ContentFetchingParams,
} from '../../hooks/useVideoGridContentFetching';
import { useVideoGridSchema } from '../../hooks/useVideoGridSchema';
import { normalizeStockItems } from '../../utils/contentUtils';
import headerAlignmentFontInputs, {
  Alignment,
  FontFamily,
  HeadingLineHeight,
} from '../../utils/headerAlignmentFontInputs';
import BottomCTAButton, { BottomCTAProps } from '../elements/BottomCTAButton';
import BuilderSection from '../layouts/BuilderSection';
import StockItemCardLoader from '../loading-skeletons/StockItemCardLoader';

interface Category {
  label: string;
  mediaType?: string;
  category?: string;
  searchTerm?: string;
  sort?: string;
  minQualityScore?: number;
  showCallToAction: boolean;
  callToAction: BottomCTAProps;
  callToActionParams?: object;
  contentMode: 'search' | 'collection' | 'mixed';
  galleryName?: string;
}

interface BrowseCategoriesProps {
  fontFamily: FontFamily;
  headingAlignment?: Alignment;
  heading: string;
  categories: Category[];
  categoriesFullWidth?: boolean;
  highlightFirstVideo?: boolean;
  numResults: string;
  customContent?: any;
}

const calculateNewSelectedCategory = (
  categories: Category[],
  prevCategories: Category[] | undefined,
  selectedCategory: number
) => {
  let categoryIndex = -1;
  if (!prevCategories || !categories) {
    return categoryIndex;
  }

  if (!prevCategories.length || !categories.length) {
    categoryIndex = 0;
    return categoryIndex;
  }

  if (categories.length === prevCategories.length) {
    categoryIndex = prevCategories.findIndex(
      (prevCategory, index) => !isEqual(prevCategory, categories[index])
    );
  } else if (categories.length > prevCategories.length) {
    categoryIndex = categories.length - 1;
  } else {
    const deletedCategoryIndex = prevCategories.findIndex(
      (prevCategory, index) => {
        return (
          index >= categories.length ||
          !isEqual(prevCategory, categories[index])
        );
      }
    );
    if (selectedCategory > deletedCategoryIndex) {
      categoryIndex = selectedCategory - 1;
    } else if (selectedCategory === deletedCategoryIndex) {
      categoryIndex = 0;
    } else {
      categoryIndex = selectedCategory;
    }
  }

  if (categoryIndex < 0 && categories.length > 0) categoryIndex = 0;
  if (categoryIndex >= categories.length && categories.length > 0)
    categoryIndex = categories.length - 1;

  return categoryIndex;
};

const BrowseCategories = (props: BrowseCategoriesProps) => {
  const {
    headingAlignment = Alignment.Center,
    heading,
    fontFamily,
    categories,
    numResults,
    customContent,
    categoriesFullWidth = false,
    highlightFirstVideo = false,
  } = props;

  const [selectedCategory, setSelectedCategory] = useState(0);
  const prevCategories = usePrevious(categories);

  const initialHookCustomContent =
    selectedCategory === 0 && customContent
      ? normalizeStockItems(customContent)
      : [];

  const currentCategory = categories[selectedCategory];

  const hookParams: ContentFetchingParams = {
    category:
      currentCategory?.mediaType === 'all-videos'
        ? undefined
        : currentCategory?.category,
    mediaType: currentCategory?.mediaType,
    searchTerm: currentCategory?.searchTerm,
    sort: currentCategory?.sort,
    minQualityScore: currentCategory?.minQualityScore,
    galleryName: currentCategory?.galleryName,
    numResults,
    contentMode: currentCategory?.contentMode || 'search',
    customContent: initialHookCustomContent,
    builderIsEditing: !!Builder.isEditing,
  };

  const {
    content: displayContent,
    loading: displayLoading,
    error,
  } = useVideoGridContentFetching(hookParams);

  useVideoGridSchema({
    heading: `${heading}${
      currentCategory?.label ? ` - ${currentCategory.label}` : ''
    }`,
    videos: displayContent || [],
    contentMode: currentCategory?.contentMode || 'search',
    category: currentCategory?.category,
    searchTerm: currentCategory?.searchTerm,
  });

  const desktopNumResults = parseInt(numResults, 10);

  useEffect(() => {
    const newSelectedCategoryIndex = calculateNewSelectedCategory(
      categories,
      prevCategories,
      selectedCategory
    );
    if (
      newSelectedCategoryIndex >= 0 &&
      newSelectedCategoryIndex < categories.length
    ) {
      setSelectedCategory(newSelectedCategoryIndex);
    } else if (categories.length === 0) {
      setSelectedCategory(0);
    }
  }, [categories, numResults]);

  const handleCategoryClick = (index: number) => {
    setSelectedCategory(index);
  };

  const handCtaClick = useCallback(() => {
    const telemetryMediaType = currentCategory?.mediaType || 'all';
    const telemetryCategoryId = currentCategory?.category || 'all';
    Telemetry.increment(
      `browseCategories.clicked.${telemetryMediaType}.${telemetryCategoryId}`
    );
  }, [currentCategory]);

  return (
    <BuilderSection
      heading={heading}
      headingClassNames={`${headingAlignment} ${fontFamily} ${HeadingLineHeight} text-3xl md:text-5xl text-gray-900 font-semibold`}
    >
      <div className="w-full space-y-6">
        <div
          className={twMerge(
            'gap-x-2 gap-y-4 flex flex-row flex-wrap items-center mb-8',
            categoriesFullWidth ? 'justify-items-stretch' : 'justify-center'
          )}
        >
          {categories?.length > 0 &&
            categories.map((category, index) => (
              <button
                onClick={() => handleCategoryClick(index)}
                tabIndex={0}
                key={`${category.label}-${index}`}
                className={twMerge(
                  'text-gray-500 no-underline transition-all border border-transparent border-solid rounded-lg px-4 py-3 text-base duration-250',
                  'focus:bg-gray-800 focus:text-white hover:bg-gray-800 hover:text-white hover:font-semibold focus:font-semibold',
                  selectedCategory === index &&
                    'bg-gray-800 text-white font-semibold',
                  categoriesFullWidth && 'flex-grow'
                )}
              >
                {category.label}
              </button>
            ))}
        </div>
        <div className="stock-item-group-wrapper card-design leading">
          {Builder.isEditing &&
            (currentCategory?.contentMode === 'collection' ||
              currentCategory?.contentMode === 'mixed') &&
            error && (
              <div
                style={{
                  background: '#fee',
                  color: '#b00',
                  padding: '12px',
                  borderRadius: '6px',
                  marginBottom: '12px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  zIndex: 10,
                }}
              >
                Invalid collection selected! This error only shows on builder
                editing mode.
              </div>
            )}
          {displayLoading ? (
            <div className="grid grid-cols-1 gap-4">
              <div className="grid gap-6 new-card-grid">
                {[...Array(desktopNumResults)].map((_, idx) => (
                  <StockItemCardLoader
                    key={`video-card-loader-${desktopNumResults + idx}`}
                    className={
                      highlightFirstVideo && idx === 0
                        ? 'row-span-2 col-span-2'
                        : ''
                    }
                  />
                ))}
              </div>
            </div>
          ) : displayContent?.length < 1 ? (
            <div>No results found{error ? `: ${error}` : ''}</div>
          ) : (
            <div className="stock-item-v2 grid grid-cols-1 gap-4">
              <div className="grid gap-6 new-card-grid">
                {displayContent.map((item, itemIndex) => (
                  <VideoStockItemCard
                    {...item}
                    className={twMerge(
                      'm-0',
                      'p-0',
                      highlightFirstVideo && itemIndex === 0
                        ? 'col-span-2 row-span-2'
                        : ''
                    )}
                    shouldInvertControlsVisibility={true}
                    thumbnailClassNames="overflow-hidden rounded-lg"
                    key={`video-stock-item-card-${
                      item.stockItem.id || itemIndex
                    }`}
                    isSelected={false}
                    page={null}
                    hideOnThreeColumnLayout={false}
                    isFocusOutlineVisible
                    shouldStockItemHaveNewFlag={false}
                    redirectToSignUpLink={false}
                    stockItemFormats={[]}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <BottomCTAButton
        show={categories[selectedCategory]?.showCallToAction}
        cta={categories[selectedCategory]?.callToAction}
        onClick={handCtaClick}
      />
    </BuilderSection>
  );
};

Builder.registerComponent(BrowseCategories, {
  name: 'Browse Categories',
  inputs: [
    {
      name: 'heading',
      type: 'string',
      required: true,
      defaultValue: 'Browse Videos',
    },
    ...headerAlignmentFontInputs,
    {
      name: 'numResults',
      friendlyName: 'Number of Results',
      type: 'string',
      enum: stockItemCardResultOptions,
      defaultValue: '6',
      required: true,
    },
    {
      name: 'categoriesFullWidth',
      type: 'boolean',
      required: false,
      defaultValue: false,
    },
    {
      name: 'highlightFirstVideo',
      type: 'boolean',
      required: false,
      defaultValue: false,
    },
    {
      name: 'categories',
      friendlyName: 'Categories',
      type: 'list',
      onChange: (options) => {
        const cats = options.get('categories');
        if (cats.length > 4) {
          options.set('categories', cats.slice(0, 4));
          alert('Categories cannot exceed 4.');
        } else if (cats.length > 0 && cats.length < 2) {
          alert('Minimum of 2 categories required if providing categories.');
        }
      },
      defaultValue: [
        {
          label: 'All',
          mediaType: 'all-videos',
          searchTerm: '',
          sort: 'most_relevant',
          contentMode: 'search',
          showCallToAction: true,
          callToAction: {
            text: 'Explore',
            href: '/',
            color: 'secondary',
            size: 'large',
            variant: 'outlined-dark',
            target: '_self',
            rel: '',
            squared: false,
          },
        },
        {
          label: '[category]',
          mediaType: 'templates',
          searchTerm: '',
          sort: 'most_relevant',
          contentMode: 'search',
          showCallToAction: true,
          callToAction: {
            text: 'Explore',
            href: '/',
            color: 'secondary',
            size: 'large',
            variant: 'outlined-dark',
            target: '_self',
            rel: '',
            squared: false,
          },
        },
      ],
      subFields: [
        {
          name: 'label',
          friendlyName: 'Label',
          type: 'string',
          defaultValue: '[Category]',
        },
        {
          name: 'contentMode',
          type: 'string',
          enum: [
            { label: 'Search', value: 'search' },
            { label: 'Collection', value: 'collection' },
            { label: 'Mixed', value: 'mixed' },
          ],
          defaultValue: 'search',
          required: true,
          friendlyName: 'Content Mode',
          onChange:
            "if (options.get('contentMode') === 'collection') { options.set('searchTerm', ''); options.set('category', ''); options.set('sort', 'most_relevant'); options.set('minQualityScore', null); } if (options.get('contentMode') === 'search') { options.set('galleryName', ''); }",
        },
        {
          name: 'mediaType',
          type: 'string',
          enum: browseCategoriesMediaTypesOptions,
          defaultValue: 'templates',
          showIf: "options.get('contentMode') !== 'collection'",
        },
        {
          name: 'category',
          friendlyName: 'Category',
          type: 'string',
          showIf:
            "options.get('mediaType') === 'footage' && options.get('contentMode') !== 'collection'",
          enum: getCategoriesForContentType('footage'),
          defaultValue: '',
        },
        {
          name: 'category',
          friendlyName: 'Category',
          type: 'string',
          showIf:
            "options.get('mediaType') === 'motion-backgrounds' && options.get('contentMode') !== 'collection'",
          enum: getCategoriesForContentType('motion-backgrounds'),
          defaultValue: '',
        },
        {
          name: 'category',
          friendlyName: 'Category',
          type: 'string',
          showIf:
            "options.get('mediaType') === 'templates' && options.get('contentMode') !== 'collection'",
          enum: getCategoriesForContentType('templates'),
          defaultValue: '',
        },
        {
          name: 'galleryName',
          type: 'string',
          defaultValue: '',
          required: true,
          showIf: "options.get('contentMode') !== 'search'",
          friendlyName: 'Collection Slug (URI)',
          helperText:
            'Enter the collection slug/URI (the part after /video/collections/ in the URL)',
          regex: {
            pattern: '^(?!/).+$',
            options: '',
            message:
              'Collection Slug (galleryName) is required and should not start with a "/". Please enter only the slug part after /video/collections/.',
          },
        },
        {
          name: 'searchTerm',
          type: 'string',
          defaultValue: '',
          showIf: "options.get('contentMode') !== 'collection'",
        },
        {
          name: 'sort',
          type: 'string',
          defaultValue: 'most_relevant',
          enum: [...defaultSortOptions, ...videoSortOptions],
          showIf: "options.get('contentMode') !== 'collection'",
        },
        {
          name: 'minQualityScore',
          type: 'number',
          defaultValue: null,
          min: 0,
          max: 1,
          showIf: "options.get('contentMode') !== 'collection'",
        },
        {
          name: 'showCallToAction',
          type: 'boolean',
          defaultValue: true,
        },
        {
          name: 'callToAction',
          type: 'object',
          showIf: "options.get('showCallToAction')",
          subFields: BottomCTASubFields,
        },
      ],
    },
  ],
});
